/**
 * 可信 host 白名单解析（防开放重定向 / 缓存投毒）
 *
 * 背景：x-forwarded-host 客户端可伪造。此前多处「x-forwarded-host 优先、无校验」直接取值，
 * 伪造值会被拼进 301/302 Location（开放重定向）或 OSS 缓存 key（缓存投毒）。
 * 本模块统一做白名单校验：只认 language-detection.ts 已维护的中英文站点名单，
 * 非法值直接丢弃并回退 fallback，杜绝伪造 host 流向 Location / 缓存。
 *
 * 注意：校验失败是「静默回退」而非拒绝请求——host 只用于跳转拼接与语言判定，
 * 回退后请求仍正常处理，只是不再跟随伪造值。
 */
import { CHINESE_HOSTS, ENGLISH_HOSTS } from './language-detection'

/** 可信 host 白名单（不含端口，小写） */
export const TRUSTED_HOSTS: readonly string[] = [...CHINESE_HOSTS, ...ENGLISH_HOSTS]

/** 默认回退域名（部署环境可用 DEFAULT_HOST 覆盖，如预发域） */
export const DEFAULT_TRUSTED_HOST = process.env.DEFAULT_HOST?.split(':')[0]?.toLowerCase() || 'www.jufair.com'

/** 单个 host 是否可信（忽略端口与大小写） */
export function isTrustedHost(host?: string): boolean {
  if (!host) return false
  const hostname = String(host).split(',')[0]!.split(':')[0]!.trim().toLowerCase()
  return TRUSTED_HOSTS.includes(hostname)
}

function normalizeHostValue(raw: string | string[] | undefined): string {
  const first = Array.isArray(raw) ? raw[0] : raw
  return String(first || '')
    .split(',')[0]
    .trim()
    .toLowerCase()
}

/**
 * 从请求头解析可信 host：优先 x-forwarded-host，其次 host，均不在白名单则回退 fallback。
 * 返回可信域名；本地开发域（localhost / 127.0.0.1）保留端口以免破坏 dev 跳转，
 * 其余域名返回不含端口的纯域名，供 Location 拼接 / getLanguageByHost / 缓存 key 使用。
 */
export function resolveTrustedRequestHost(
  headers: Record<string, string | string[] | undefined>,
  fallback?: string
): string {
  const forwarded = normalizeHostValue(headers['x-forwarded-host'])
  if (forwarded && isTrustedHost(forwarded)) {
    return keepLocalhostPort(forwarded)
  }
  const hostHeader = normalizeHostValue(headers.host)
  if (hostHeader && isTrustedHost(hostHeader)) {
    return keepLocalhostPort(hostHeader)
  }
  return fallback || DEFAULT_TRUSTED_HOST
}

/** localhost / 127.0.0.1 保留端口（dev 跳转需要）；其余域名去端口 */
function keepLocalhostPort(host: string): string {
  const hostname = host.split(':')[0]!
  if (hostname === 'localhost' || hostname === '127.0.0.1') return host
  return hostname
}
